import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import client from '../../../lib/feathers';
import LibationAttachments from './LibationAttachments';
import VintageFormats from './VintageFormats';
import UserDataDrillDownModal from './UserDataDrillDownModal';

const SCALAR_FIELDS = [
  { key: 'abv', label: 'ABV', format: (v) => `${v}%` },
  { key: 'msrp', label: 'MSRP', format: (v) => `$${v}` },
  { key: 'production', label: 'Production', format: (v) => `${v} cases` },
  { key: 'soil', label: 'Soil' },
  { key: 'elevation', label: 'Elevation' },
  { key: 'harvestedAt', label: 'Harvest Date', format: (v) => formatDate(v) },
  { key: 'bottledAt', label: 'Bottled Date', format: (v) => formatDate(v) },
  { key: 'drinkByStart', label: 'Drink Window Start', format: (v) => formatDate(v) },
  { key: 'drinkByEnd', label: 'Drink Window End', format: (v) => formatDate(v) },
];

const MARKDOWN_FIELDS = [
  { key: 'tastingNotes', label: 'Tasting Notes' },
  { key: 'winemakeing', label: 'Winemaking Notes' },
  { key: 'aging', label: 'Aging Process' },
  { key: 'clones', label: 'Clones' },
  { key: 'farming', label: 'Farming Practices' },
  { key: 'lifespan', label: 'Drinking Window / Lifespan' },
  { key: 'scores', label: 'Scores' },
  { key: 'pairings', label: 'Pairings' },
];

const TOTAL_VINTAGE_FIELDS = SCALAR_FIELDS.length + MARKDOWN_FIELDS.length;

function formatDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function isFilled(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
}

function computeVintageCompleteness(lv) {
  let filled = 0;
  for (const f of SCALAR_FIELDS) if (isFilled(lv[f.key])) filled += 1;
  for (const f of MARKDOWN_FIELDS) if (isFilled(lv[f.key])) filled += 1;
  return { filled, total: TOTAL_VINTAGE_FIELDS };
}

const NotSet = () => <span className="italic text-gray-400">Not set</span>;

const TRIGGER_LABEL = {
  merge: 'merged from',
  vintage_move: 'vintage moved from',
  manual: 'added manually',
  pipeline: 'pipeline-discovered',
  import: 'imported',
};

// Aliases live on libation.aliases as a JSONB array:
//   [{ alias, addedAt, addedBy, donorLibationId? }, ...]
// Surfaces what other names should resolve back to this libation so
// future matcher calls (stage 03, bottle-scan, c7 webhook, manual
// entry) don't re-create the dupe.
const AliasesSection = ({ aliases }) => {
  if (!aliases || !Array.isArray(aliases) || aliases.length === 0) {
    return (
      <div className="mt-6">
        <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Aliases</h4>
        <div className="text-sm italic text-gray-400">No aliases yet — names accumulate here when a dupe libation is merged into this one.</div>
      </div>
    );
  }
  return (
    <div className="mt-6">
      <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">
        Aliases ({aliases.length})
      </h4>
      <ul className="text-sm divide-y divide-gray-100 border border-gray-200 rounded-md">
        {aliases.map((a, idx) => (
          <li key={`${a.alias}-${idx}`} className="px-3 py-2 flex justify-between items-baseline gap-3">
            <span className="font-mono text-gray-800 break-all">{a.alias}</span>
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {TRIGGER_LABEL[a.addedBy] || a.addedBy || '—'}
              {a.addedAt ? ` · ${new Date(a.addedAt).toISOString().slice(0, 10)}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const NeedsReviewPill = () => (
  <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">
    Needs Review
  </span>
);

const StatusChip = ({ status }) => {
  if (status === 'active') return <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">Active</span>;
  if (status === 'inactive') return <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-600/20">Inactive</span>;
  if (status === 'library') return <span className="inline-flex items-center rounded-md bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-600/20">Library</span>;
  if (status === 'member') return <span className="inline-flex items-center rounded-md bg-yellow-50 px-2 py-1 text-xs font-medium text-yellow-700 ring-1 ring-inset ring-yellow-600/20">Member</span>;
  return null;
};

const FieldRow = ({ label, value, format }) => {
  const filled = isFilled(value);
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">
        {filled ? (format ? format(value) : value) : <NotSet />}
      </dd>
    </div>
  );
};

const MARKDOWN_BODY_CLASS = [
  'text-sm text-gray-800 leading-relaxed',
  '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
  '[&_strong]:font-semibold [&_em]:italic',
  '[&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2',
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2',
  '[&_li]:my-0.5',
  '[&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-1',
  '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1',
  '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
  '[&_a]:text-primary [&_a]:underline',
].join(' ');

const MarkdownSection = ({ title, body }) => (
  <div className="border-t border-gray-100 pt-4">
    <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">{title}</h4>
    {isFilled(body) ? (
      <div className={MARKDOWN_BODY_CLASS}>
        <ReactMarkdown>{body}</ReactMarkdown>
      </div>
    ) : (
      <div className="text-sm"><NotSet /></div>
    )}
  </div>
);

const CompletenessChip = ({ filled, total }) => {
  const pct = total === 0 ? 0 : Math.round((filled / total) * 100);
  return (
    <div className="flex items-center gap-2 min-w-[12rem]">
      <span className="text-xs text-gray-500 whitespace-nowrap">
        {filled} of {total} fields populated
      </span>
      <div className="flex-1 h-1 bg-gray-200 overflow-hidden">
        <div className="h-1 bg-primary" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// One row showing the user-data counts attached to an LV (or to the
// whole libation in totals form). Highlighted amber when any count is
// non-zero so the operator can see risk at a glance.
//
// In admin role, each count (with value > 0) is rendered as a button
// that opens UserDataDrillDownModal — clicking "sips 5" lists the
// users + per-record context. Non-admin (industry) sees nothing — the
// counts surface admin/* services and aren't intended for industry.
const UserDataCounts = ({ counts, role, onClickType, compact = false }) => {
  if (role !== 'admin') return null;
  if (!counts) {
    return <span className="text-xs text-gray-400 italic">no data</span>;
  }
  // 'flights' has no drill-down service yet (would need to traverse
  // flightLibationVintage → flight → user). Surface the count but
  // don't make it clickable until that path exists.
  const items = [
    { type: 'sips',     label: 'sips',    value: counts.sips ?? counts.userSips ?? 0, clickable: true },
    { type: 'cellar',   label: 'cellar',  value: counts.cellarEntries ?? 0,           clickable: true },
    { type: 'scans',    label: 'scans',   value: counts.scans ?? 0,                   clickable: true },
    { type: 'flights',  label: 'flights', value: counts.flights ?? counts.flightEntries ?? 0, clickable: false },
  ];
  const hasAny = items.some((it) => it.value > 0);
  return (
    <div className={`inline-flex items-center gap-3 text-xs font-mono ${hasAny ? 'text-amber-800' : 'text-gray-400'} ${compact ? '' : 'flex-wrap'}`}>
      {items.map((it) => {
        const isLive = it.clickable && it.value > 0 && typeof onClickType === 'function';
        if (isLive) {
          return (
            <button
              key={it.label}
              type="button"
              onClick={() => onClickType(it.type)}
              className="font-semibold underline decoration-dotted underline-offset-2 hover:text-amber-900"
              title={`See who has ${it.label} on this libation`}
            >
              {it.label} {it.value}
            </button>
          );
        }
        return (
          <span key={it.label} className={it.value > 0 ? 'font-semibold' : ''}>
            {it.label} {it.value}
          </span>
        );
      })}
    </div>
  );
};

const VintageCard = ({ libation, lv, role, lid, onCountClick }) => {
  const year = lv.vintage?.year === 9999 ? 'NV' : lv.vintage?.year;
  const { filled, total } = computeVintageCompleteness(lv);
  const lvCounts = libation.counts?.byVintage?.[lv.id];

  // Local state for the per-vintage "Enrich this vintage" button.
  // POSTs a kind='spot_libation' row into the enrichment queue. The
  // pipeline-side worker (next slice) will pick it up and run Ollama
  // Search + LLM extraction to fill missing scalars. While that
  // worker doesn't exist yet, the queue captures intent correctly
  // and the Data Health card on the listing surfaces it as pending.
  const [enrichState, setEnrichState] = useState('idle'); // 'idle' | 'enqueuing' | 'queued' | 'error'
  const [enrichMsg, setEnrichMsg] = useState(null);

  const isIndustrySource = lv.source === 'industry';

  const enqueueEnrichment = async () => {
    if (enrichState === 'enqueuing') return;
    setEnrichState('enqueuing');
    setEnrichMsg(null);
    try {
      await client.service('admin/listing-enrichment-run').create({
        listingId: libation.listingId,
        libationVintageId: lv.id,
        kind: 'spot_libation',
        triggeredBy: 'manual',
        outcome: 'pending',
        priority: 5,
      });
      setEnrichState('queued');
      setEnrichMsg('Queued — runs on next worker tick.');
    } catch (e) {
      const msg = e.message || '';
      if (/unique/i.test(msg) || /SequelizeUniqueConstraintError/.test(msg)) {
        setEnrichState('queued');
        setEnrichMsg('Already queued for enrichment.');
      } else {
        setEnrichState('error');
        setEnrichMsg(msg || 'Failed to enqueue');
      }
    }
  };

  return (
    <div className="bg-white shadow sm:rounded-lg p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-gray-200">
        <div className="flex items-center gap-4 flex-wrap">
          <h3 className="text-3xl font-bold text-gray-900 leading-none">{year}</h3>
          {lv.needsReview && <NeedsReviewPill />}
          {lv.source && lv.source !== 'user' && (
            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
              Source: {lv.source}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <CompletenessChip filled={filled} total={total} />
          {role === 'admin' && !isIndustrySource && (
            <button
              type="button"
              onClick={enqueueEnrichment}
              disabled={enrichState === 'enqueuing' || enrichState === 'queued'}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              title="Queue an Ollama-Search-backed enrichment pass for this vintage"
            >
              {enrichState === 'enqueuing' ? 'Queuing…'
                : enrichState === 'queued' ? 'Queued ✓'
                : 'Enrich this vintage'}
            </button>
          )}
          {role === 'admin' && isIndustrySource && (
            <span
              className="text-xs text-gray-400 italic"
              title="Industry-set vintages are not auto-enriched"
            >
              Industry-set
            </span>
          )}
          <Link
            to={`/${role}/listings/${lid}/wines/${libation.id}/${lv.id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
          >
            Edit Vintage
          </Link>
        </div>
      </div>
      {enrichMsg && (
        <div className={`mt-2 text-xs ${enrichState === 'error' ? 'text-red-700' : 'text-gray-500'}`}>
          {enrichMsg}
        </div>
      )}

      {role === 'admin' && (
        <div className="mt-4">
          <span className="text-xs uppercase tracking-wide text-gray-500 mr-3">User data</span>
          <UserDataCounts
            counts={lvCounts}
            role={role}
            onClickType={(type) => onCountClick && onCountClick({ type, libationVintageId: lv.id, year })}
          />
        </div>
      )}

      <dl className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4">
        {SCALAR_FIELDS.map((f) => (
          <FieldRow key={f.key} label={f.label} value={lv[f.key]} format={f.format} />
        ))}
      </dl>

      <div className="mt-6 space-y-4">
        {MARKDOWN_FIELDS.map((f) => (
          <MarkdownSection key={f.key} title={f.label} body={lv[f.key]} />
        ))}
      </div>

      <div className="mt-6">
        <VintageFormats libationVintageId={lv.id} />
      </div>

      <div className="mt-6">
        <LibationAttachments
          targetType="libationVintage"
          targetId={lv.id}
          libationId={libation.id}
          vintages={libation.libationVintages}
          title="Vintage media"
          description="Bottle photos, label images, and trade documents specific to this vintage. Use Move… to send one up to the wine or across to another vintage."
        />
      </div>
    </div>
  );
};

export default function LibationReview() {
  const { lid, wid } = useParams();
  const user = useSelector((state) => state.auth.user);
  const [libation, setLibation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Drill-down modal state — admin-only click on a user-data count.
  // { type, libationVintageId?, year? } when open; null when closed.
  const [drillDown, setDrillDown] = useState(null);

  useEffect(() => {
    if (!user || !wid) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const service = client.service(`${user.role}/libation`);
        const result = await service.get(wid);
        if (!cancelled) setLibation(result);
      } catch (e) {
        console.error('Error loading libation review:', e);
        if (!cancelled) setError(e.message || 'Failed to load wine.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user, wid]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (error || !libation) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">{error || 'Wine not found.'}</p>
      </div>
    );
  }

  const role = user.role;
  const vintages = libation.libationVintages || [];

  return (
    <div className="space-y-6">
      {/* HEADER STRIP */}
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-gray-900 leading-tight">{libation.name}</h2>
              {libation.needsReview && <NeedsReviewPill />}
              <StatusChip status={libation.status} />
            </div>
            <div className="mt-2 flex items-center gap-2 flex-wrap text-sm text-gray-600">
              {libation.varietal?.name && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ring-1 ring-inset"
                  style={{
                    backgroundColor: libation.varietal.color ? `${libation.varietal.color}20` : '#f3f4f6',
                    color: libation.varietal.color || '#374151',
                    boxShadow: libation.varietal.color ? `inset 0 0 0 1px ${libation.varietal.color}40` : undefined,
                  }}
                >
                  {libation.varietal.name}
                </span>
              )}
              {libation.libationType?.name && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                  {libation.libationType.name}
                </span>
              )}
              {libation.source && libation.source !== 'user' && (
                <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                  Source: {libation.source}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/${role}/listings/${lid}/wines`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Back to Wines
            </Link>
            <Link
              to={`/${role}/listings/${lid}/wines/${libation.id}/edit`}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-red-700"
            >
              Edit Libation
            </Link>
          </div>
        </div>
      </div>

      {/* LIBATION CARD */}
      <div className="bg-white shadow sm:rounded-lg p-6">
        <div className="md:grid md:grid-cols-3 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Wine Information</h3>
            <p className="mt-1 text-sm text-gray-500">Libation-level data shared across every vintage.</p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            {role === 'admin' && libation.counts && (
              <div className="mb-6 p-3 rounded-md bg-gray-50 border border-gray-200">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
                  User data (libation-wide totals)
                </div>
                <UserDataCounts
                  counts={libation.counts}
                  role={role}
                  onClickType={(type) => {
                    const lvIds = (libation.libationVintages || []).map((v) => v.id);
                    setDrillDown({ type, lvIds, label: 'all vintages' });
                  }}
                />
              </div>
            )}
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              <FieldRow
                label="Winery"
                value={libation.listing?.name}
                format={(v) => (
                  <Link
                    to={`/${role}/listings/${libation.listing?.id || lid}`}
                    className="text-primary hover:text-red-900"
                  >
                    {v}
                  </Link>
                )}
              />
              <FieldRow label="AVA" value={libation.ava} />
              <FieldRow label="Type" value={libation.libationType?.name} />
              <FieldRow label="Varietal" value={libation.varietal?.name} />
              <FieldRow
                label="Status"
                value={libation.status}
                format={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
              />
              <FieldRow label="Source" value={libation.source} />
              {role === 'admin' && (
                <FieldRow
                  label="Added By"
                  value={libation.createdBy}
                  format={(v) => (
                    <span className="capitalize">
                      {v.firstName} {v.lastName} ({v.role})
                    </span>
                  )}
                />
              )}
              <FieldRow
                label="Added On"
                value={libation.createdAt}
                format={(v) => formatDate(v)}
              />
              <FieldRow
                label="Updated On"
                value={libation.updatedAt}
                format={(v) => formatDate(v)}
              />
            </dl>

            <div className="mt-6">
              <MarkdownSection title="Description" body={libation.description} />
            </div>

            <AliasesSection aliases={libation.aliases} />
          </div>
        </div>
      </div>

      {/* LIBATION-WIDE MEDIA */}
      <LibationAttachments
        targetType="libation"
        targetId={libation.id}
        libationId={libation.id}
        vintages={libation.libationVintages}
        title="Libation-wide media"
        description="Bottle photos, generic labels, and trade documents that apply to every vintage of this wine. Use Move… to send one down to a specific vintage."
      />

      {/* VINTAGES */}
      <div>
        <div className="flex items-baseline justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Vintages ({vintages.length})
          </h3>
          {vintages.length > 0 && (
            <p className="text-sm text-gray-500">Sorted newest first.</p>
          )}
        </div>
        {vintages.length === 0 ? (
          <div className="bg-white shadow sm:rounded-lg p-6 text-sm text-gray-500 italic">
            No vintages yet. Add one from the{' '}
            <Link to={`/${role}/listings/${lid}/wines`} className="text-primary hover:text-red-900 not-italic">
              Wines list
            </Link>
            .
          </div>
        ) : (
          <div className="space-y-6">
            {vintages.map((lv) => (
              <VintageCard
                key={lv.id}
                libation={libation}
                lv={lv}
                role={role}
                lid={lid}
                onCountClick={({ type, libationVintageId, year }) => setDrillDown({ type, libationVintageId, label: year ? `${year}` : 'vintage' })}
              />
            ))}
          </div>
        )}
      </div>

      <UserDataDrillDownModal
        open={!!drillDown}
        onClose={() => setDrillDown(null)}
        type={drillDown?.type}
        libationVintageId={drillDown?.libationVintageId}
        lvIds={drillDown?.lvIds}
        title={drillDown ? `${drillDown.type} on ${libation.name} — ${drillDown.label}` : ''}
      />
    </div>
  );
}
